import * as PIXI from 'pixi.js';
import { Connector as ConnectorData, Pin } from '../types';
import { BRAND, CONNECTOR, CONNECTOR_PINS, LABEL_RESOLUTION } from '../utils/constants';
import { connectorLayout, totalHeight, wireRowAt, PinRowLayout, PinWireCounts, WIRE_LINE_HEIGHT } from '../utils/connectorLayout';
import { formatGauge } from '../utils/length';

/** Height of the draggable title bar; flush with the body so its border reads as the first row divider. */
const HEADER_HEIGHT = CONNECTOR_PINS.BODY_Y;
/** Top of the first pin row, relative to the connector. */
const BODY_Y = CONNECTOR_PINS.BODY_Y;
/** Centre x and radius of a pin dot. */
const DOT_X = 12;
const DOT_R = 4;
/** Left inset of the pin label, and the left edge of the wire-name column. */
const PIN_LABEL_X = 22;
const WIRE_COL_X = 60;
/** Colour swatch drawn at the start of a wire row: size, inset and the wire-name inset after it. */
const SWATCH_SIZE = 10;
const SWATCH_X = 2;
const WIRE_NAME_X = SWATCH_X + SWATCH_SIZE + 6;
/** Outline colour of a wire colour swatch. */
const SWATCH_OUTLINE = 0xced6e0;
/** Clickable width of the wire-name column. */
const WIRE_COL_WIDTH = CONNECTOR.WIDTH - WIRE_COL_X - 8;
/** Width of the left gutter (dot + label) that starts or terminates a wire. */
const GUTTER_WIDTH = WIRE_COL_X;
/** Label shown on the always-present row that adds a pin on the fly. */
const ADD_PIN_LABEL = '+ pin';
/** Outline colour drawn around a selected connector. */
const SELECTION_COLOR = BRAND.GOLD;
/** Grey fill of a connected pin dot and outline of an unconnected one. */
const PIN_FILL = 0xb2bec3;
const PIN_OUTLINE = 0x95a5a6;
/** Light separator drawn between table rows in the connector body. */
const ROW_DIVIDER_COLOR = 0xecf0f1;
/** Two left presses within this window on a wire row count as a double-click. */
const DOUBLE_CLICK_MS = 300;

/** A wire row as shown inside a connector: its name, colour and optional conductor gauge. */
export interface ConnectorWireRow {
  id: string;
  name: string;
  /** Conductor cross-section in mm², shown alongside the name when set. */
  gauge?: number;
  /** Base insulation colour (hex string), shown as a swatch. */
  color?: string;
  /** Optional second colour; when it differs from {@link color} the swatch is striped. */
  stripeColor?: string;
}

/** The full pin-and-wire content of a connector body. */
export interface ConnectorContent {
  pins: Pin[];
  /** Wires terminating on each pin at this connector, keyed by pin id. */
  wiresByPin: Map<string, ConnectorWireRow[]>;
  /** Wires connected to this connector but not terminating on one of its pins. */
  unassigned: ConnectorWireRow[];
}

/**
 * What a point in the pin gutter resolves to: an existing pin, or the row that
 * adds a new pin. Returned by {@link Connector.pinAt} so the wire-completion
 * handler can terminate a wire on the clicked pin.
 */
export type PinHit = { kind: 'pin'; pinId: string } | { kind: 'add' };

export class Connector extends PIXI.Container {
  private data: ConnectorData;
  private background: PIXI.Graphics;
  private header: PIXI.Graphics;
  private nameText: PIXI.Text;
  private content: PIXI.Container;
  private selectionOutline: PIXI.Graphics;
  /** Current pin instances, cached so {@link pinAt} can map a click to a pin. */
  private pins: Pin[] = [];
  /** Laid-out pin rows, cached so {@link pinAt} can map a point to a pin. */
  private pinRows: PinRowLayout[] = [];
  /** Wire rows per pin and the unassigned block, cached so {@link wireAt} can map a point to a wire. */
  private wireRowsByPin: Map<string, ConnectorWireRow[]> = new Map();
  private unassignedRows: ConnectorWireRow[] = [];
  /** Combined height of the pin rows, marking the top of the unassigned block. */
  private pinsHeight = 0;
  /** Top of the trailing add-pin row, relative to the table origin. */
  private addRowTop = 0;
  private currentHeight = 0;
  private selected = false;

  constructor(connectorData: ConnectorData) {
    super();

    this.data = connectorData;
    this.x = connectorData.xPosition;
    this.y = connectorData.yPosition;

    this.background = new PIXI.Graphics();
    this.header = new PIXI.Graphics();
    this.nameText = new PIXI.Text({ text: '', style: {} });
    this.content = new PIXI.Container();
    this.selectionOutline = new PIXI.Graphics();

    this.setupVisuals();
    this.setupInteraction();
  }

  private setupVisuals(): void {
    // Draggable title bar.
    this.header.rect(0, 0, CONNECTOR.WIDTH, HEADER_HEIGHT);
    this.header.fill(0xf1f3f5);
    this.header.moveTo(0, HEADER_HEIGHT);
    this.header.lineTo(CONNECTOR.WIDTH, HEADER_HEIGHT);
    this.header.stroke({ width: 1, color: ROW_DIVIDER_COLOR });

    this.nameText = new PIXI.Text({
      text: this.data.name,
      style: new PIXI.TextStyle({ fontSize: 16, fontWeight: 'bold', fill: 0x2d3436 }),
      resolution: LABEL_RESOLUTION
    });
    this.nameText.x = 10;
    this.nameText.y = 10;
    // Let clicks fall through to the header so the whole title bar is draggable.
    this.nameText.eventMode = 'none';

    this.content.x = 0;
    this.content.y = BODY_Y;
    this.selectionOutline.eventMode = 'none';

    // A starting frame; updateContent resizes it to fit the real content.
    this.drawFrame(totalHeight(0, 0));

    this.addChild(this.background);
    this.addChild(this.header);
    this.addChild(this.nameText);
    this.addChild(this.content);
    this.addChild(this.selectionOutline);
  }

  /** Redraws the card background and hit area to the given height. */
  private drawFrame(height: number): void {
    this.currentHeight = height;
    this.background.clear();
    this.background.rect(0, 0, CONNECTOR.WIDTH, height);
    this.background.fill(0xffffff);
    this.background.stroke({ width: 2, color: 0x636e72 });
    this.hitArea = new PIXI.Rectangle(0, 0, CONNECTOR.WIDTH, height);
  }

  /** Draws or clears a highlight box marking this connector as selected. */
  setSelected(selected: boolean): void {
    this.selected = selected;
    this.selectionOutline.clear();
    if (!selected) {
      return;
    }
    this.selectionOutline.rect(-3, -3, CONNECTOR.WIDTH + 6, this.currentHeight + 6);
    this.selectionOutline.stroke({ width: 2, color: SELECTION_COLOR });
  }

  private setupInteraction(): void {
    // The box is interactive so it reads clicks on its pins and wire rows, but it
    // is not draggable; dragging is limited to the header (see setupHeaderDrag).
    // Right-clicks are resolved by the canvas context-menu handler, which hit-tests
    // connectors and their wire rows from the native contextmenu event.
    this.eventMode = 'static';
    this.cursor = 'default';

    this.setupHeaderDrag();
  }

  /** Makes the title bar the drag handle for moving the connector on the canvas. */
  private setupHeaderDrag(): void {
    this.header.eventMode = 'static';
    this.header.cursor = 'grab';
    this.header.hitArea = new PIXI.Rectangle(0, 0, CONNECTOR.WIDTH, HEADER_HEIGHT);

    let isDragging = false;
    const dragOffset = { x: 0, y: 0 };

    this.header.on('pointerdown', (event) => {
      // Only the left button starts a drag; other buttons bubble up to the
      // connector (e.g. right-click opens the context menu).
      if (event.button !== 0) {
        return;
      }

      isDragging = true;
      const localPos = this.toLocal(event.global);
      dragOffset.x = localPos.x;
      dragOffset.y = localPos.y;

      this.emit('dragStart');
      event.stopPropagation();

      const canvas = this.getCanvas();
      if (canvas) {
        canvas.style.pointerEvents = 'none';
      }
      document.body.style.cursor = 'grabbing';

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) {
          return;
        }
        const canvas = this.getCanvas();
        if (!canvas) {
          return;
        }
        const rect = canvas.getBoundingClientRect();
        const screenX = e.clientX - rect.left;
        const screenY = e.clientY - rect.top;
        if (this.parent) {
          const worldPos = this.parent.toLocal({ x: screenX, y: screenY });
          this.x = worldPos.x - dragOffset.x;
          this.y = worldPos.y - dragOffset.y;
          this.updateData();
        }
      };

      const handleMouseUp = () => {
        isDragging = false;
        this.emit('dragEnd');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        const canvas = this.getCanvas();
        if (canvas) {
          canvas.style.pointerEvents = 'auto';
        }
        document.body.style.cursor = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
  }

  private updateData(): void {
    this.data.xPosition = this.x;
    this.data.yPosition = this.y;
    this.emit('positionChanged', this.x, this.y);
  }

  /**
   * Renders the connector body as a single table: one row per pin, each carrying
   * the wires that terminate on it (rows expand for multi-wire pins). Unconnected
   * pins show an outlined dot, connected pins a filled one. A trailing "+ pin" row
   * adds a pin on the fly. The card grows vertically to fit its content.
   */
  updateContent(content: ConnectorContent): void {
    this.pins = content.pins;
    const counts: PinWireCounts = {};
    content.pins.forEach(pin => {
      counts[pin.id] = content.wiresByPin.get(pin.id)?.length ?? 0;
    });
    const layout = connectorLayout(content.pins, counts);
    this.pinRows = layout.rows;
    this.wireRowsByPin = content.wiresByPin;
    this.unassignedRows = content.unassigned;
    this.pinsHeight = layout.pinsHeight;
    this.addRowTop = layout.pinsHeight + content.unassigned.length * WIRE_LINE_HEIGHT;

    this.content.removeChildren();
    this.content.addChild(this.buildRowDividers(layout.rows, content.unassigned.length));

    content.pins.forEach((pin, index) => {
      const row = layout.rows[index];
      this.content.addChild(this.buildPinRow(row, pin, content.wiresByPin.get(pin.id) ?? []));
    });

    content.unassigned.forEach((wire, index) => {
      const line = this.buildWireLine(wire);
      line.y = layout.pinsHeight + index * WIRE_LINE_HEIGHT;
      this.content.addChild(line);
    });

    this.content.addChild(this.buildAddRow(this.addRowTop));

    this.drawFrame(totalHeight(layout.pinsHeight, content.unassigned.length));
    if (this.selected) {
      this.setSelected(true);
    }
  }

  /**
   * Draws a light separator at the top boundary of every table row after the
   * first: one row per pin (however many wires it stacks), then the unassigned
   * wire lines and the trailing add-pin row.
   */
  private buildRowDividers(rows: PinRowLayout[], unassignedCount: number): PIXI.Graphics {
    const heights = [
      ...rows.map(r => r.height),
      ...Array(unassignedCount).fill(WIRE_LINE_HEIGHT),
      WIRE_LINE_HEIGHT
    ];
    const dividers = new PIXI.Graphics();
    dividers.eventMode = 'none';
    let y = 0;
    heights.forEach((height, index) => {
      if (index > 0) {
        dividers.moveTo(0, y);
        dividers.lineTo(CONNECTOR.WIDTH, y);
      }
      y += height;
    });
    dividers.stroke({ width: 1, color: ROW_DIVIDER_COLOR });
    return dividers;
  }

  /** Builds one pin row: its clickable gutter, dot, label and stacked wire names. */
  private buildPinRow(row: PinRowLayout, pin: Pin, wires: ConnectorWireRow[]): PIXI.Container {
    const container = new PIXI.Container();
    container.y = row.top;

    // The left gutter starts a wire from this pin (or terminates one on it).
    const gutter = new PIXI.Container();
    gutter.eventMode = 'static';
    gutter.cursor = 'pointer';
    gutter.hitArea = new PIXI.Rectangle(0, 0, GUTTER_WIDTH, row.height);
    gutter.on('pointerdown', (event) => {
      if (event.button !== 0) {
        return;
      }
      event.stopPropagation();
      this.emit('pinPointerDown', pin.id);
    });
    container.addChild(gutter);

    // The pin dot and number sit on the first wire line, so the pin shares a row
    // with its first wire; any further wires stack beneath within the same row.
    const dot = new PIXI.Graphics();
    dot.circle(DOT_X, WIRE_LINE_HEIGHT / 2, DOT_R);
    if (wires.length > 0) {
      dot.fill(PIN_FILL);
      dot.stroke({ width: 1, color: PIN_OUTLINE });
    } else {
      dot.stroke({ width: 1.5, color: PIN_OUTLINE });
    }
    dot.eventMode = 'none';
    container.addChild(dot);

    const label = new PIXI.Text({
      text: pin.label,
      style: new PIXI.TextStyle({ fontSize: 12, fill: 0x2d3436 }),
      resolution: LABEL_RESOLUTION
    });
    label.anchor.set(0, 0.5);
    label.x = PIN_LABEL_X;
    label.y = WIRE_LINE_HEIGHT / 2;
    label.eventMode = 'none';
    container.addChild(label);

    wires.forEach((wire, index) => {
      const line = this.buildWireLine(wire);
      line.y = index * WIRE_LINE_HEIGHT;
      container.addChild(line);
    });

    return container;
  }

  /** Builds the wire colour swatch, striped diagonally when a differing second colour is set. */
  private buildWireSwatch(wire: ConnectorWireRow): PIXI.Graphics {
    const top = (WIRE_LINE_HEIGHT - SWATCH_SIZE) / 2;
    const swatch = new PIXI.Graphics();
    swatch.roundRect(SWATCH_X, top, SWATCH_SIZE, SWATCH_SIZE, 2);
    swatch.fill(wire.color ?? '#636e72');
    swatch.stroke({ width: 1, color: SWATCH_OUTLINE });
    if (wire.stripeColor && wire.stripeColor !== wire.color) {
      swatch.moveTo(SWATCH_X + 1, top + SWATCH_SIZE - 1);
      swatch.lineTo(SWATCH_X + SWATCH_SIZE - 1, top + 1);
      swatch.stroke({ width: 2, color: wire.stripeColor });
    }
    swatch.eventMode = 'none';
    return swatch;
  }

  /** Builds one interactive wire-name line in the wire column. */
  private buildWireLine(wire: ConnectorWireRow): PIXI.Container {
    const line = new PIXI.Container();
    line.x = WIRE_COL_X;

    const background = new PIXI.Graphics();
    background.rect(0, 0, WIRE_COL_WIDTH, WIRE_LINE_HEIGHT);
    background.fill(0xf1f2f6);
    background.alpha = 0;
    line.addChild(background);

    line.addChild(this.buildWireSwatch(wire));

    const text = new PIXI.Text({
      text: wire.name,
      style: new PIXI.TextStyle({ fontSize: 12, fill: 0x2d3436 }),
      resolution: LABEL_RESOLUTION
    });
    text.anchor.set(0, 0.5);
    text.x = WIRE_NAME_X;
    text.y = WIRE_LINE_HEIGHT / 2;
    text.eventMode = 'none';
    line.addChild(text);

    if (wire.gauge !== undefined) {
      const gauge = new PIXI.Text({
        text: formatGauge(wire.gauge),
        style: new PIXI.TextStyle({ fontSize: 10, fill: 0x95a5a6 }),
        resolution: LABEL_RESOLUTION
      });
      gauge.anchor.set(1, 0.5);
      gauge.x = WIRE_COL_WIDTH - 4;
      gauge.y = WIRE_LINE_HEIGHT / 2;
      gauge.eventMode = 'none';
      line.addChild(gauge);
    }

    line.eventMode = 'static';
    line.cursor = 'pointer';
    line.hitArea = new PIXI.Rectangle(0, 0, WIRE_COL_WIDTH, WIRE_LINE_HEIGHT);

    line.on('pointerenter', () => {
      background.alpha = 0.3;
      this.emit('wireRowHover', wire.id);
    });
    line.on('pointerleave', () => {
      background.alpha = 0;
      this.emit('wireRowHoverEnd', wire.id);
    });
    let lastTapAt = 0;
    line.on('pointerdown', (event) => {
      // Right-clicks are handled by the canvas context-menu handler; only the
      // left-button double-click to edit is resolved here.
      if (event.button !== 0) {
        return;
      }
      event.stopPropagation();
      const now = Date.now();
      const isDoubleClick = now - lastTapAt < DOUBLE_CLICK_MS;
      lastTapAt = now;
      if (isDoubleClick) {
        this.emit('wireRowDoubleClick', wire.id);
      }
    });

    return line;
  }

  /** Builds the trailing "+ pin" row that adds a pin on the fly. */
  private buildAddRow(top: number): PIXI.Container {
    const row = new PIXI.Container();
    row.y = top;
    row.eventMode = 'static';
    row.cursor = 'pointer';
    row.hitArea = new PIXI.Rectangle(0, 0, GUTTER_WIDTH, WIRE_LINE_HEIGHT);
    row.on('pointerdown', (event) => {
      if (event.button !== 0) {
        return;
      }
      event.stopPropagation();
      this.emit('addPinPointerDown');
    });

    const label = new PIXI.Text({
      text: ADD_PIN_LABEL,
      style: new PIXI.TextStyle({ fontSize: 11, fill: 0x95a5a6, fontStyle: 'italic' }),
      resolution: LABEL_RESOLUTION
    });
    label.x = 2;
    label.y = 1;
    label.eventMode = 'none';
    row.addChild(label);
    return row;
  }

  /**
   * Resolves a connector-local point to the pin gutter row under it: an existing
   * pin, the "add pin" row, or null when the point is outside the gutter. Lets the
   * wire-completion handler terminate a wire on the clicked pin.
   */
  pinAt(localX: number, localY: number): PinHit | null {
    if (localX < 0 || localX > GUTTER_WIDTH) {
      return null;
    }
    const bodyY = localY - BODY_Y;
    if (bodyY < 0) {
      return null;
    }
    const row = this.pinRows.find(r => bodyY >= r.top && bodyY < r.top + r.height);
    if (row) {
      return { kind: 'pin', pinId: row.pinId };
    }
    if (bodyY >= this.addRowTop && bodyY < this.addRowTop + WIRE_LINE_HEIGHT) {
      return { kind: 'add' };
    }
    return null;
  }

  /** True when a world-space point lies within this connector's card. */
  containsPoint(worldX: number, worldY: number): boolean {
    const localX = worldX - this.x;
    const localY = worldY - this.y;
    return localX >= 0 && localX <= CONNECTOR.WIDTH && localY >= 0 && localY <= this.currentHeight;
  }

  /**
   * Resolves a world-space point to the id of the wire row under it, or null when
   * the point is not over a wire name in this connector's wire column. Lets the
   * canvas context menu offer wire actions for the exact wire the user clicked.
   */
  wireAt(worldX: number, worldY: number): string | null {
    const localX = worldX - this.x;
    if (localX < WIRE_COL_X || localX > WIRE_COL_X + WIRE_COL_WIDTH) {
      return null;
    }
    const bodyY = worldY - this.y - BODY_Y;
    const hit = wireRowAt(this.pinRows, this.pinsHeight, this.unassignedRows.length, bodyY);
    if (!hit) {
      return null;
    }
    const rows = hit.pinId === null ? this.unassignedRows : this.wireRowsByPin.get(hit.pinId) ?? [];
    return rows[hit.index]?.id ?? null;
  }

  /**
   * Local-space centre of the wire row carrying the given wire, or null when the
   * wire is not shown on this connector. The inverse of {@link wireAt}: it lets
   * an end-to-end test click a specific wire row on the otherwise opaque canvas.
   */
  wireRowLocalCenter(wireId: string): { x: number; y: number } | null {
    const x = WIRE_COL_X + WIRE_COL_WIDTH / 2;
    for (const row of this.pinRows) {
      const index = (this.wireRowsByPin.get(row.pinId) ?? []).findIndex(w => w.id === wireId);
      if (index >= 0) {
        return { x, y: BODY_Y + row.top + index * WIRE_LINE_HEIGHT + WIRE_LINE_HEIGHT / 2 };
      }
    }
    const unassignedIndex = this.unassignedRows.findIndex(w => w.id === wireId);
    if (unassignedIndex >= 0) {
      return { x, y: BODY_Y + this.pinsHeight + unassignedIndex * WIRE_LINE_HEIGHT + WIRE_LINE_HEIGHT / 2 };
    }
    return null;
  }

  getData(): ConnectorData {
    return { ...this.data };
  }

  setName(name: string): void {
    this.data.name = name;
    this.nameText.text = name;
  }

  highlight(enabled: boolean): void {
    this.background.tint = enabled ? 0xffff99 : 0xffffff;
  }

  private getGlobalStage(): PIXI.Container | null {
    let current: PIXI.Container | null = this.parent;
    while (current && current.parent) {
      current = current.parent;
    }
    return current;
  }

  private getCanvas(): HTMLCanvasElement | null {
    const stage = this.getGlobalStage();
    if (stage && (stage as any).renderer) {
      return (stage as any).renderer.canvas;
    }

    const canvases = document.getElementsByTagName('canvas');
    if (canvases.length > 0) {
      return canvases[0];
    }

    return null;
  }
}
